import { useEffect, useRef, useState, type ReactNode } from "react";
import { Wrench, Gauge, Image as ImageIcon, Sparkles, ArrowRight, AlertTriangle, Globe, Lightbulb } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "react-router-dom";
import ServiceHero, { Highlight } from "./ServiceHero";
import IncludesExcludes from "./IncludesExcludes";

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setIsVisible(true); obs.unobserve(el); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, isVisible };
}

function AnimateIn({ children, className = "", delay = 0 }: { children: ReactNode; className?: string; delay?: number }) {
  const { ref, isVisible } = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? "translateY(0)" : "translateY(30px)",
      transition: `opacity 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
    }}>{children}</div>
  );
}

const processSteps = [
  { n: "01", title: "Contratas el servicio", desc: "Eliges Mantenimiento Web en tu plan." },
  { n: "02", title: "Nos das acceso", desc: "A tu web." },
  { n: "03", title: "Optimizamos cada mes", desc: "Imágenes, velocidad y salud técnica." },
  { n: "04", title: "Te recomendamos mejoras", desc: "Para que tu web siga creciendo." },
];

const MantenimientoWebDemo = () => {
  return (
    <div className="space-y-28 overflow-hidden">
      {/* ━━━ HERO ━━━ */}
      <ServiceHero
        eyebrow="Mantenimiento Web"
        title={<>Mantenimiento <Highlight>Web</Highlight></>}
        description={
          <>
            Tu web rápida, optimizada y al día. Nos ocupamos de la salud técnica
            de tu sitio para que{" "}
            <strong className="text-foreground font-semibold">
              cargue rápido y rinda al máximo,
            </strong>{" "}
            mes a mes.
          </>
        }
        primaryCta={{ label: "Contratar Mantenimiento Web", href: "/#pricing" }}
        secondaryCta={{ label: "Agendar reunión", href: "/#contact" }}
        decoration={
          <div className="flex items-center gap-4">
            <div
              className="w-16 h-16 rounded-2xl bg-card border border-border shadow-md flex items-center justify-center text-primary"
              style={{ animation: "float 6s ease-in-out infinite" }}
            >
              <Wrench className="w-8 h-8" />
            </div>
            <div className="text-sm text-muted-foreground font-body">
              Velocidad · Imágenes · Salud técnica
            </div>
          </div>
        }
        includes={["Imágenes optimizadas", "Velocidad de carga", "Recomendaciones", "Webs propias o de terceros"]}
      />

      {/* ━━━ INCLUYE / NO INCLUYE ━━━ */}
      <IncludesExcludes
        includes={[
          "Minimización y optimización de imágenes",
          "Optimización de velocidad de carga",
          "Recomendaciones de mejora continuas",
          "Válido para webs hechas por nosotros <strong>o por terceros</strong>",
        ]}
        excludes={[
          "Creación de webs o páginas nuevas",
          "Dominio ni hosting",
          "Cambios de textos, imágenes o productos",
          "Plugins de pago",
          "Monitorización de caídas ni resolución si la web se cae (no alojamos tu web)",
          "Reparación por hackeos o roturas ajenas a nuestro trabajo",
        ]}
      />

      {/* ━━━ SECCIÓN DESTACADA ━━━ */}
      <section className="max-w-5xl mx-auto px-4">
        <AnimateIn>
          <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-primary/[0.08] via-primary/[0.04] to-transparent shadow-xl shadow-primary/10">
            <CardContent className="p-10 md:p-14">
              <div className="flex flex-col md:flex-row items-start gap-8">
                <div className="w-16 h-16 rounded-2xl bg-primary text-primary-foreground flex items-center justify-center shrink-0 shadow-lg shadow-primary/30">
                  <Gauge className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-4">
                    Una web rápida vende más
                  </h3>
                  <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
                    La velocidad de carga afecta directamente a tus ventas y a tu
                    posicionamiento en Google. Cada segundo de carga extra es un
                    porcentaje de visitantes que se va. Nuestro mantenimiento
                    mensual mantiene tu web ligera, optimizada y rindiendo al
                    máximo.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </AnimateIn>
      </section>

      {/* ━━━ PARA QUÉ WEBS ━━━ */}
      <section className="max-w-5xl mx-auto px-4">
        <AnimateIn>
          <div className="text-center mb-10">
            <Badge variant="secondary" className="mb-4">Para qué webs</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-3">
              Webs propias o de terceros
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              El servicio aplica tanto a webs creadas por Vibra como a webs
              hechas por terceros.
            </p>
          </div>
        </AnimateIn>

        <div className="grid md:grid-cols-2 gap-5">
          <AnimateIn>
            <div className="rounded-2xl border border-border bg-card p-6 h-full hover:border-primary/40 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                <Globe className="w-6 h-6" />
              </div>
              <h4 className="font-bold text-lg mb-2">Webs hechas por Vibra</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Las conocemos al detalle. Mantenimiento continuo para que sigan
                rindiendo al máximo después del lanzamiento.
              </p>
            </div>
          </AnimateIn>

          <AnimateIn delay={0.1}>
            <div className="rounded-2xl border border-border bg-card p-6 h-full hover:border-primary/40 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                <ImageIcon className="w-6 h-6" />
              </div>
              <h4 className="font-bold text-lg mb-2">Webs de terceros</h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Auditamos tu web actual y aplicamos las optimizaciones que más
                impacto tengan en velocidad y rendimiento.
              </p>
            </div>
          </AnimateIn>
        </div>

        {/* Nota importante */}
        <AnimateIn delay={0.15}>
          <div className="mt-6 rounded-2xl border-2 border-amber-300 bg-amber-50/60 p-6 flex gap-4">
            <div className="w-11 h-11 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div>
              <h5 className="font-bold mb-1.5">
                Si tu web está para rehacerse, te lo decimos
              </h5>
              <p className="text-sm text-foreground/80 leading-relaxed">
                Si tu web de terceros está en mal estado y lo que necesita es
                prácticamente rehacerse, este servicio no es la solución: te lo
                diremos con honestidad y te propondremos el{" "}
                <Link
                  to="/servicios/web-corporativa"
                  className="text-primary font-semibold underline underline-offset-2 hover:no-underline"
                >
                  servicio puntual de Web Corporativa
                </Link>
                .
              </p>
            </div>
          </div>
        </AnimateIn>
      </section>

      {/* ━━━ CÓMO TRABAJAMOS ━━━ */}
      <section className="max-w-5xl mx-auto px-4">
        <AnimateIn>
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">Proceso</Badge>
            <h2 className="text-3xl md:text-4xl font-bold mb-3">Cómo trabajamos</h2>
          </div>
        </AnimateIn>

        <div className="grid md:grid-cols-4 gap-4">
          {processSteps.map((step, i) => (
            <AnimateIn key={step.n} delay={i * 0.08}>
              <div className="rounded-2xl border border-border bg-card p-6 h-full hover:border-primary/40 transition-colors">
                <div className="font-display text-3xl text-primary mb-3">{step.n}</div>
                <h4 className="font-bold text-sm mb-2">{step.title}</h4>
                <p className="text-xs text-muted-foreground leading-relaxed">{step.desc}</p>
              </div>
            </AnimateIn>
          ))}
        </div>
      </section>

      {/* ━━━ CTA FINAL ━━━ */}
      <section className="max-w-4xl mx-auto px-4">
        <AnimateIn>
          <Card className="border-primary/20 overflow-hidden relative">
            <div className="absolute -inset-10 bg-primary/5 rounded-[3rem] blur-3xl pointer-events-none" />
            <CardContent className="p-10 md:p-16 text-center relative z-10">
              <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center text-primary mx-auto mb-6">
                <Sparkles className="w-8 h-8" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                ¿Tu web va lenta?
              </h2>
              <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
                Elige tu plan y empezamos a optimizarla este mismo mes.
              </p>
              <Link
                to="/#pricing"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-primary text-primary-foreground font-medium hover:shadow-glow transition-all"
              >
                Contratar ahora
                <ArrowRight className="w-4 h-4" />
              </Link>
            </CardContent>
          </Card>
        </AnimateIn>
      </section>
    </div>
  );
};

export default MantenimientoWebDemo;
